(() => {
  const reveal = () => {
    const elements = document.querySelectorAll('[data-aevum-reveal]:not(.is-visible)');

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    elements.forEach((element) => observer.observe(element));
  };

  const syncAevumHeader = () => {
    const opening = document.querySelector('.aevum-stillness');
    const headerGroup = document.querySelector('#header-group');
    if (!opening || !headerGroup) return;

    const solidAt = Math.max(40, opening.offsetTop + opening.offsetHeight - headerGroup.offsetHeight);
    headerGroup.classList.toggle('aevum-header--solid', window.scrollY >= solidAt);
  };

  const syncMobileCta = () => {
    const cta = document.querySelector('[data-aevum-mobile-cta]');
    const opening = document.querySelector('.aevum-opening, .aevum-hero');
    if (!cta || !opening) return;

    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const showAfter = opening.offsetTop + opening.offsetHeight * 0.72;
    cta.classList.toggle('is-visible', mobile && window.scrollY >= showAfter);
  };

  const setupFloatingPurchaseCta = () => {
    const floatingCta = document.querySelector('.aevum-floating-buy');
    if (!floatingCta) return;

    floatingCta.aevumInlineObserver?.disconnect();

    const inlineCtas = document.querySelectorAll(
      '.aevum-opening .aevum-button, .aevum-bonus .aevum-button, .aevum-spotlight .aevum-button'
    );
    if (!inlineCtas.length || !('IntersectionObserver' in window)) return;

    const visibleInlineCtas = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            visibleInlineCtas.add(entry.target);
          } else {
            visibleInlineCtas.delete(entry.target);
          }
        });
        floatingCta.classList.toggle('is-suppressed', visibleInlineCtas.size > 0);
      },
      { rootMargin: '0px 0px -78px 0px', threshold: [0, 0.35, 1] }
    );

    inlineCtas.forEach((cta) => observer.observe(cta));
    floatingCta.aevumInlineObserver = observer;
  };

  const setupFloatingOfferTimer = () => {
    document.querySelectorAll('[data-aevum-offer-timer]:not([data-aevum-ready])').forEach((timer) => {
      timer.dataset.aevumReady = 'true';

      const durationSeconds = Number(timer.dataset.durationSeconds) || 900;
      const storageKey = 'aevum-floating-offer-end-v2';
      let endTime = Date.now() + durationSeconds * 1000;

      try {
        const storedEndTime = Number(window.sessionStorage.getItem(storageKey));
        if (Number.isFinite(storedEndTime) && storedEndTime > 0) {
          endTime = storedEndTime;
        } else {
          window.sessionStorage.setItem(storageKey, String(endTime));
        }
      } catch {
        // The countdown still works when browser storage is unavailable.
      }

      const syncTimer = () => {
        const remainingSeconds = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;

        timer.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
        timer.setAttribute('datetime', `PT${remainingSeconds}S`);

        if (remainingSeconds === 0 && timer.aevumOfferInterval) {
          window.clearInterval(timer.aevumOfferInterval);
          timer.aevumOfferInterval = null;
        }
      };

      syncTimer();
      if (endTime > Date.now()) {
        timer.aevumOfferInterval = window.setInterval(syncTimer, 1000);
      }
    });
  };

  const hydrateVideo = (video) => {
    if (!video || video.dataset.aevumHydrated === 'true') return;
    const source = video.dataset.src;
    if (!source) return;
    video.src = source;
    video.dataset.aevumHydrated = 'true';
    video.load();
  };

  const setupLazyVideos = () => {
    document.querySelectorAll('[data-aevum-video-play]:not([data-aevum-ready])').forEach((button) => {
      button.dataset.aevumReady = 'true';
      button.dataset.aevumPlayLabel = button.textContent.trim();
      button.addEventListener('click', () => {
        const card = button.closest('.aevum-video-card');
        const video = card?.querySelector('[data-aevum-lazy-video]');
        if (!video) return;

        const mobile = window.matchMedia('(max-width: 749px)').matches;

        if (mobile && !video.paused) {
          video.pause();
          card.classList.remove('is-playing');
          button.querySelector('span')?.replaceChildren('▶');
          button.setAttribute('aria-label', button.dataset.aevumPlayLabel);
          return;
        }

        hydrateVideo(video);
        video.controls = !mobile;
        card.classList.add('is-playing');
        if (mobile) {
          button.querySelector('span')?.replaceChildren('Ⅱ');
          button.setAttribute('aria-label', 'Pause video');
        }
        video.play().catch(() => {
          card.classList.remove('is-playing');
          button.querySelector('span')?.replaceChildren('▶');
          button.setAttribute('aria-label', button.dataset.aevumPlayLabel);
        });
      });
    });

    document.querySelectorAll('[data-aevum-lazy-video]:not([data-aevum-listening])').forEach((video) => {
      video.dataset.aevumListening = 'true';
      video.addEventListener('play', () => {
        document.querySelectorAll('[data-aevum-lazy-video]').forEach((otherVideo) => {
          if (otherVideo !== video && !otherVideo.paused) otherVideo.pause();
        });
      });
      video.addEventListener('pause', () => {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const card = video.closest('.aevum-video-card');
        const button = card?.querySelector('[data-aevum-video-play]');
        card?.classList.remove('is-playing');
        video.controls = false;
        button?.querySelector('span')?.replaceChildren('▶');
        if (button) button.setAttribute('aria-label', button.dataset.aevumPlayLabel || 'Play video');
      });
    });
  };

  const setupMobileHorizontalTracks = () => {
    const selector = '.aevum-proof .aevum-community__grid, .aevum-reviews-track';

    document.querySelectorAll(`${selector}:not([data-aevum-axis-lock])`).forEach((track) => {
      track.dataset.aevumAxisLock = 'true';
      let gesture = null;
      let suppressClick = false;

      const finishGesture = () => {
        if (!gesture) return;

        if (gesture.axis === 'x') {
          const trackRect = track.getBoundingClientRect();
          const cards = [...track.children].filter((child) => child.matches('.aevum-video-card, .aevum-review-card'));
          const nearest = cards.reduce((best, card) => {
            const distance = Math.abs(card.getBoundingClientRect().left - trackRect.left);
            return !best || distance < best.distance ? { card, distance } : best;
          }, null);

          track.classList.remove('is-aevum-dragging');
          if (nearest) {
            const delta = nearest.card.getBoundingClientRect().left - trackRect.left;
            track.scrollTo({ left: track.scrollLeft + delta, behavior: 'smooth' });
          }
          if (suppressClick) {
            window.setTimeout(() => { suppressClick = false; }, 400);
          }
        }

        gesture = null;
      };

      track.addEventListener('touchstart', (event) => {
        if (!window.matchMedia('(max-width: 749px)').matches || event.touches.length !== 1) return;
        const touch = event.touches[0];
        gesture = {
          id: touch.identifier,
          x: touch.clientX,
          y: touch.clientY,
          scrollLeft: track.scrollLeft,
          axis: null,
        };
      }, { passive: true });

      track.addEventListener('touchmove', (event) => {
        if (!gesture) return;
        const touch = Array.from(event.touches).find((item) => item.identifier === gesture.id);
        if (!touch) return;

        const deltaX = touch.clientX - gesture.x;
        const deltaY = touch.clientY - gesture.y;

        if (!gesture.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 16) {
          gesture.axis = Math.abs(deltaX) > Math.abs(deltaY) * 1.75 ? 'x' : 'y';
          if (gesture.axis === 'x') {
            track.classList.add('is-aevum-dragging');
          }
        }

        if (gesture.axis !== 'x') return;
        suppressClick = Math.abs(deltaX) > 10;
        track.scrollLeft = gesture.scrollLeft - deltaX;
        if (event.cancelable) event.preventDefault();
      }, { passive: false });

      track.addEventListener('touchend', finishGesture);
      track.addEventListener('touchcancel', finishGesture);
      track.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, true);
    });
  };

  const setupMobileVideoGallery = () => {
    document.querySelectorAll('[data-aevum-video-nav]:not([data-aevum-ready])').forEach((nav) => {
      const section = nav.closest('.aevum-community__intro');
      const track = section?.querySelector('[data-aevum-video-track]');
      const cards = track ? [...track.querySelectorAll('.aevum-video-card')] : [];
      const prev = nav.querySelector('[data-aevum-video-prev]');
      const next = nav.querySelector('[data-aevum-video-next]');
      const current = nav.querySelector('[data-aevum-video-current]');
      if (!track || !cards.length || !prev || !next || !current) return;

      nav.dataset.aevumReady = 'true';

      const activeIndex = () => {
        const trackLeft = track.getBoundingClientRect().left;
        return cards.reduce((best, card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Infinity }).index;
      };

      const sync = () => {
        const index = activeIndex();
        current.textContent = String(index + 1);
        prev.disabled = index === 0;
        next.disabled = index === cards.length - 1;
      };

      const move = (direction) => {
        const index = Math.max(0, Math.min(cards.length - 1, activeIndex() + direction));
        track.scrollTo({ left: cards[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      };

      prev.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  };

  let headerTicking = false;
  const requestHeaderSync = () => {
    if (headerTicking) return;
    headerTicking = true;
    window.requestAnimationFrame(() => {
      syncAevumHeader();
      syncMobileCta();
      headerTicking = false;
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    reveal();
    setupLazyVideos();
    setupMobileHorizontalTracks();
    setupMobileVideoGallery();
    setupFloatingPurchaseCta();
    setupFloatingOfferTimer();
    syncAevumHeader();
    syncMobileCta();
  });
  document.addEventListener('shopify:section:load', () => {
    reveal();
    setupLazyVideos();
    setupMobileHorizontalTracks();
    setupMobileVideoGallery();
    setupFloatingPurchaseCta();
    setupFloatingOfferTimer();
    syncAevumHeader();
    syncMobileCta();
  });
  window.addEventListener('scroll', requestHeaderSync, { passive: true });
  window.addEventListener('resize', requestHeaderSync, { passive: true });
})();
